import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  PlusCircle,
  Search,
  Edit3,
  Trash2,
  Eye,
  FileText,
  AlertCircle,
  CheckCircle,
  Download,
  Settings,
  Filter,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../ui/card";
import { Badge } from "../ui/badge";
import { Alert, AlertDescription } from "../ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { saveAs } from "file-saver";
import {
  getAllOffertes,
  deleteOfferte as apiDeleteOfferte,
  downloadOffertePDF,
} from "../../api/offerte";

// Color scheme constants
const COLORS = {
  primary: "#BFFF00", // Neon green
  secondary: "#1E4FCF", // Blue
  accent: "#FF6B00", // Orange
  dark: "#05081A", // Dark blue/black
};

const OffertesManager = () => {
  const navigate = useNavigate();
  const [offertes, setOffertes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [offerteToDelete, setOfferteToDelete] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [currentActionOfferteId, setCurrentActionOfferteId] = useState(null);

  const fetchOffertes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllOffertes();
      setOffertes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching offertes:", err);
      const errorMessage =
        err.message ||
        (err.data && err.data.message) ||
        "Kon offertes niet laden. Probeer het later opnieuw.";
      setError(errorMessage);
      setOffertes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOffertes();
  }, [fetchOffertes]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  const filteredOffertes = offertes.filter((offerte) => {
    const klantNaam = offerte.klantNaam || "";
    const nummer = offerte.nummer || "";
    const status = offerte.status || "";

    const matchesSearch =
      klantNaam.toLowerCase().includes(searchTerm) ||
      nummer.toLowerCase().includes(searchTerm);
    const matchesStatus =
      statusFilter === "all" || status.toLowerCase() === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const openDeleteDialog = (e, offerte) => {
    e.stopPropagation();
    setOfferteToDelete(offerte);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteOfferte = async () => {
    if (!offerteToDelete || !offerteToDelete.id) return;
    setCurrentActionOfferteId(offerteToDelete.id);
    try {
      await apiDeleteOfferte(offerteToDelete.id);
      setOffertes((prev) => prev.filter((o) => o.id !== offerteToDelete.id));
      setSuccessMessage(
        `Offerte ${offerteToDelete.nummer || offerteToDelete.id} succesvol verwijderd.`,
      );
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Error deleting offerte:", err);
      const errorMessage =
        err.message ||
        (err.data && err.data.message) ||
        "Kon offerte niet verwijderen.";
      setError(errorMessage);
    } finally {
      setDeleteDialogOpen(false);
      setOfferteToDelete(null);
      setCurrentActionOfferteId(null);
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (String(status).toLowerCase()) {
      case "concept":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "verzonden":
        return "bg-blue-100 text-blue-800 border-blue-300";
      case "geaccepteerd":
        return "bg-green-100 text-green-800 border-green-300";
      case "geweigerd":
        return "bg-red-100 text-red-800 border-red-300";
      case "verlopen":
        return "bg-gray-100 text-gray-800 border-gray-300";
      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("nl-BE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (typeof amount !== "number" && typeof amount !== "string") return "-";
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return "-";
    return numAmount.toLocaleString("nl-BE", {
      style: "currency",
      currency: "EUR",
    });
  };

  const handleAction = (e, action, offerteId) => {
    e.stopPropagation();
    action(offerteId);
  };

  const handleNavigateToNewOfferte = () => {
    navigate("/dashboard/offertes/nieuw");
  };

  const handleViewOfferte = (offerteId) => {
    navigate(`/dashboard/offertes/details/${offerteId}`);
  };

  const handleEditOfferte = (offerteId) => {
    navigate(`/dashboard/offertes/edit/${offerteId}`);
  };

  const handleNavigateToSettings = () => {
    navigate("/dashboard/offertes/settings");
  };

  const handleDownloadPDF = async (offerteId) => {
    const offerteToDownload = offertes.find((o) => o.id === offerteId);
    if (!offerteToDownload) {
      setError("Offerte niet gevonden voor download.");
      return;
    }

    setIsDownloading(true);
    setCurrentActionOfferteId(offerteId);
    setError(null);
    setSuccessMessage("");

    try {
      const blob = await downloadOffertePDF(offerteId);
      saveAs(blob, `Offerte-${offerteToDownload.nummer || offerteId}.pdf`);
      setSuccessMessage(
        `Offerte ${offerteToDownload.nummer || offerteId} succesvol gedownload.`,
      );
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error(`Error downloading PDF for offerte ${offerteId}:`, err);
      const displayError =
        err.message ||
        (err.data && err.data.message) ||
        "Kon PDF niet downloaden.";
      setError(displayError);
    } finally {
      setIsDownloading(false);
      setCurrentActionOfferteId(null);
    }
  };

  // Count offertes by status
  const statusCounts = {
    total: offertes.length,
    concept: offertes.filter(
      (offerte) => String(offerte.status).toLowerCase() === "concept",
    ).length,
    verzonden: offertes.filter(
      (offerte) => String(offerte.status).toLowerCase() === "verzonden",
    ).length,
    geaccepteerd: offertes.filter(
      (offerte) => String(offerte.status).toLowerCase() === "geaccepteerd",
    ).length,
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="relative">
          <div className="w-12 h-12 rounded-full border-3 border-gray-100 border-t-[#BFFF00] border-r-[#BFFF00] animate-spin"></div>
          <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
            <span className="text-xs font-medium text-gray-400">LADEN</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header section with stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white border border-gray-200 shadow-sm rounded-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center">
              <FileText className="h-4 w-4 mr-1.5 text-[#1E4FCF]" /> Totaal
              offertes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {statusCounts.total}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-gray-200 shadow-sm rounded-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center">
              <AlertCircle className="h-4 w-4 mr-1.5 text-[#1E4FCF]" /> Concept
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <div className="h-2.5 w-2.5 rounded-full bg-yellow-400 mr-2"></div>
              <span className="text-2xl font-bold text-gray-900">
                {statusCounts.concept}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-gray-200 shadow-sm rounded-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center">
              <Download className="h-4 w-4 mr-1.5 text-[#1E4FCF]" /> Verzonden
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <div className="h-2.5 w-2.5 rounded-full bg-blue-500 mr-2"></div>
              <span className="text-2xl font-bold text-gray-900">
                {statusCounts.verzonden}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-gray-200 shadow-sm rounded-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center">
              <CheckCircle className="h-4 w-4 mr-1.5 text-[#1E4FCF]" />{" "}
              Geaccepteerd
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <div className="h-2.5 w-2.5 rounded-full bg-green-500 mr-2"></div>
              <span className="text-2xl font-bold text-gray-900">
                {statusCounts.geaccepteerd}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col gap-4"
      >
        <div className="flex justify-between items-center">
          <div className="relative">
            <h1 className="text-2xl font-bold text-gray-900">Offertes</h1>
            <div className="absolute bottom-0 left-0 w-1/2 h-1 bg-[#BFFF00] -z-10 transform translate-y-1 opacity-60"></div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleNavigateToSettings}
              variant="outline"
              className="border-gray-300 text-gray-700 hover:bg-gray-50/80 rounded-md"
              disabled={isDownloading}
            >
              <Settings className="mr-2 h-4 w-4" /> Instellingen
            </Button>
            <Button
              onClick={handleNavigateToNewOfferte}
              className="bg-[#BFFF00] hover:bg-[#BFFF00]/90 text-[#05081A] font-medium rounded-md"
              disabled={isDownloading}
            >
              <PlusCircle className="mr-2 h-4 w-4" /> Nieuwe Offerte
            </Button>
          </div>
        </div>

        {/* Notifications */}
        {error && (
          <Alert
            variant="destructive"
            className="border border-red-200 bg-red-50 text-red-800 rounded-lg"
          >
            <div className="flex gap-2 items-center">
              <AlertCircle className="h-4 w-4 text-red-500" />
              <AlertDescription className="font-medium">
                {error}
              </AlertDescription>
            </div>
          </Alert>
        )}

        {successMessage && (
          <Alert className="border border-green-200 bg-green-50 text-green-800 rounded-lg">
            <div className="flex gap-2 items-center">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <AlertDescription className="font-medium">
                {successMessage}
              </AlertDescription>
            </div>
          </Alert>
        )}

        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Zoek op klant of nummer..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="pl-10 border-gray-200 focus:border-[#BFFF00] focus:ring-[#BFFF00]/10 rounded-md"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-[180px] border-gray-200 focus:border-[#BFFF00] focus:ring-[#BFFF00]/10 rounded-md">
              <Filter className="h-4 w-4 mr-2 text-gray-400" />
              <SelectValue placeholder="Filter op status" />
            </SelectTrigger>
            <SelectContent className="border border-gray-200">
              <SelectItem value="all">Alle offertes</SelectItem>
              <SelectItem value="concept">Concept</SelectItem>
              <SelectItem value="verzonden">Verzonden</SelectItem>
              <SelectItem value="geaccepteerd">Geaccepteerd</SelectItem>
              <SelectItem value="geweigerd">Geweigerd</SelectItem>
              <SelectItem value="verlopen">Verlopen</SelectItem>
            </SelectContent>
          </Select>

          <span className="text-sm text-gray-500 ml-auto">
            {filteredOffertes.length} offerte
            {filteredOffertes.length !== 1 ? "s" : ""} gevonden
          </span>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="border border-gray-200 shadow-sm rounded-lg overflow-hidden">
          {filteredOffertes.length === 0 ? (
            <CardContent className="pt-6">
              <div className="text-center py-12 px-4">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <FileText className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Geen offertes gevonden
                </h3>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  {searchTerm || statusFilter !== "all"
                    ? "Probeer uw zoekterm of filter aan te passen."
                    : "Maak een nieuwe offerte aan om te beginnen of controleer of er offertes zijn."}
                </p>
                <Button
                  onClick={handleNavigateToNewOfferte}
                  className="bg-[#BFFF00] hover:bg-[#BFFF00]/90 text-[#05081A] font-medium rounded-md"
                >
                  <PlusCircle className="mr-2 h-4 w-4" /> Nieuwe offerte
                  aanmaken
                </Button>
              </div>
            </CardContent>
          ) : (
            <>
              {/* Mobile card layout */}
              <div className="sm:hidden space-y-3 p-4">
                {filteredOffertes.map((offerte) => (
                  <div
                    key={offerte.id}
                    className="rounded-xl border border-gray-100 bg-white p-4 cursor-pointer active:bg-gray-50 transition-colors"
                    onClick={() => {
                      if (
                        !(
                          isDownloading && currentActionOfferteId === offerte.id
                        )
                      ) {
                        handleViewOfferte(offerte.id);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[#1E4FCF]">
                          {offerte.nummer || "N/A"}
                        </p>
                        <p className="text-sm text-gray-700 mt-0.5">
                          {offerte.klantNaam || "N/A"}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-gray-900">
                          {formatCurrency(offerte.totaalInclBTW)}
                        </p>
                        <Badge
                          variant="outline"
                          className={`text-xs font-medium px-2 py-0.5 rounded-full border mt-1 ${getStatusBadgeVariant(offerte.status)}`}
                        >
                          {offerte.status
                            ? offerte.status.charAt(0).toUpperCase() +
                              offerte.status.slice(1)
                            : "Onbekend"}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span>{formatDate(offerte.offerteDatum)}</span>
                        <span>→ {formatDate(offerte.vervalDatum)}</span>
                      </div>
                      <div onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-gray-500"
                              disabled={
                                isDownloading &&
                                currentActionOfferteId === offerte.id
                              }
                            >
                              <svg
                                width="15"
                                height="15"
                                viewBox="0 0 15 15"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M8.625 2.5C8.625 3.12132 8.12132 3.625 7.5 3.625C6.87868 3.625 6.375 3.12132 6.375 2.5C6.375 1.87868 6.87868 1.375 7.5 1.375C8.12132 1.375 8.625 1.87868 8.625 2.5ZM8.625 7.5C8.625 8.12132 8.12132 8.625 7.5 8.625C6.87868 8.625 6.375 8.12132 6.375 7.5C6.375 6.87868 6.87868 6.375 7.5 6.375C8.12132 6.375 8.625 6.87868 8.625 7.5ZM8.625 12.5C8.625 13.1213 8.12132 13.625 7.5 13.625C6.87868 13.625 6.375 13.12132 6.375 12.5C6.375 11.8787 6.87868 11.375 7.5 11.375C8.12132 11.375 8.625 11.8787 8.625 12.5Z"
                                  fill="currentColor"
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                ></path>
                              </svg>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="bg-white border-gray-100 shadow-lg rounded-lg w-48"
                          >
                            <DropdownMenuItem
                              onClick={(e) =>
                                handleAction(e, handleViewOfferte, offerte.id)
                              }
                              className="text-gray-700 hover:bg-gray-50 focus:bg-gray-50 cursor-pointer"
                            >
                              <Eye className="mr-2 h-4 w-4 text-[#1E4FCF]" />{" "}
                              Bekijken
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) =>
                                handleAction(e, handleEditOfferte, offerte.id)
                              }
                              className="text-gray-700 hover:bg-gray-50 focus:bg-gray-50 cursor-pointer"
                            >
                              <Edit3 className="mr-2 h-4 w-4 text-[#1E4FCF]" />{" "}
                              Bewerken
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadPDF(offerte.id);
                              }}
                              disabled={
                                isDownloading &&
                                currentActionOfferteId === offerte.id
                              }
                              className="text-gray-700 hover:bg-gray-50 focus:bg-gray-50 cursor-pointer"
                            >
                              <Download className="mr-2 h-4 w-4 text-[#1E4FCF]" />
                              {isDownloading &&
                              currentActionOfferteId === offerte.id
                                ? "Downloaden..."
                                : "Download PDF"}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-gray-100" />
                            <DropdownMenuItem
                              onClick={(e) => openDeleteDialog(e, offerte)}
                              className="text-red-600 hover:bg-red-50 focus:bg-red-50 cursor-pointer"
                            >
                              <Trash2 className="mr-2 h-4 w-4" /> Verwijderen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table layout */}
              <div className="hidden sm:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 hover:bg-gray-50">
                      <TableHead className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nummer
                      </TableHead>
                      <TableHead className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Klant
                      </TableHead>
                      <TableHead className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Datum
                      </TableHead>
                      <TableHead className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vervaldatum
                      </TableHead>
                      <TableHead className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">
                        Totaal
                      </TableHead>
                      <TableHead className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                        Status
                      </TableHead>
                      <TableHead className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">
                        Acties
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="divide-y divide-gray-100">
                    {filteredOffertes.map((offerte) => (
                      <TableRow
                        key={offerte.id}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => {
                          if (
                            !(
                              isDownloading &&
                              currentActionOfferteId === offerte.id
                            )
                          ) {
                            handleViewOfferte(offerte.id);
                          }
                        }}
                      >
                        <TableCell className="px-4 py-3.5 text-sm font-medium text-[#1E4FCF]">
                          {offerte.nummer || "N/A"}
                        </TableCell>
                        <TableCell className="px-4 py-3.5 text-sm text-gray-700">
                          {offerte.klantNaam || "N/A"}
                        </TableCell>
                        <TableCell className="px-4 py-3.5 text-sm text-gray-500">
                          {formatDate(offerte.offerteDatum)}
                        </TableCell>
                        <TableCell className="px-4 py-3.5 text-sm text-gray-500">
                          {formatDate(offerte.vervalDatum)}
                        </TableCell>
                        <TableCell className="px-4 py-3.5 text-sm text-gray-700 text-right">
                          {formatCurrency(offerte.totaalInclBTW)}
                        </TableCell>
                        <TableCell className="px-4 py-3.5 text-center">
                          <Badge
                            variant="outline"
                            className={`text-xs font-medium px-2 py-0.5 rounded-full border ${getStatusBadgeVariant(offerte.status)}`}
                          >
                            {offerte.status
                              ? offerte.status.charAt(0).toUpperCase() +
                                offerte.status.slice(1)
                              : "Onbekend"}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-3.5 text-sm text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-gray-500 hover:text-gray-700"
                                disabled={
                                  isDownloading &&
                                  currentActionOfferteId === offerte.id
                                }
                                onClick={(e) => e.stopPropagation()}
                              >
                                <svg
                                  width="15"
                                  height="15"
                                  viewBox="0 0 15 15"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M8.625 2.5C8.625 3.12132 8.12132 3.625 7.5 3.625C6.87868 3.625 6.375 3.12132 6.375 2.5C6.375 1.87868 6.87868 1.375 7.5 1.375C8.12132 1.375 8.625 1.87868 8.625 2.5ZM8.625 7.5C8.625 8.12132 8.12132 8.625 7.5 8.625C6.87868 8.625 6.375 8.12132 6.375 7.5C6.375 6.87868 6.87868 6.375 7.5 6.375C8.12132 6.375 8.625 6.87868 8.625 7.5ZM8.625 12.5C8.625 13.1213 8.12132 13.625 7.5 13.625C6.87868 13.625 6.375 13.12132 6.375 12.5C6.375 11.8787 6.87868 11.375 7.5 11.375C8.12132 11.375 8.625 11.8787 8.625 12.5Z"
                                    fill="currentColor"
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                  ></path>
                                </svg>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="bg-white border-gray-100 shadow-lg rounded-lg w-48"
                            >
                              <DropdownMenuItem
                                onClick={(e) =>
                                  handleAction(e, handleViewOfferte, offerte.id)
                                }
                                className="text-gray-700 hover:bg-gray-50 focus:bg-gray-50 cursor-pointer"
                                disabled={
                                  isDownloading &&
                                  currentActionOfferteId === offerte.id
                                }
                              >
                                <Eye className="mr-2 h-4 w-4 text-[#1E4FCF]" />{" "}
                                Bekijken
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) =>
                                  handleAction(e, handleEditOfferte, offerte.id)
                                }
                                className="text-gray-700 hover:bg-gray-50 focus:bg-gray-50 cursor-pointer"
                                disabled={
                                  isDownloading &&
                                  currentActionOfferteId === offerte.id
                                }
                              >
                                <Edit3 className="mr-2 h-4 w-4 text-[#1E4FCF]" />{" "}
                                Bewerken
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadPDF(offerte.id);
                                }}
                                disabled={
                                  isDownloading &&
                                  currentActionOfferteId === offerte.id
                                }
                                className="text-gray-700 hover:bg-gray-50 focus:bg-gray-50 cursor-pointer"
                              >
                                <Download className="mr-2 h-4 w-4 text-[#1E4FCF]" />
                                {isDownloading &&
                                currentActionOfferteId === offerte.id
                                  ? "Downloaden..."
                                  : "Download PDF"}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator className="bg-gray-100" />
                              <DropdownMenuItem
                                onClick={(e) => openDeleteDialog(e, offerte)}
                                className="text-red-600 hover:bg-red-50 focus:bg-red-50 cursor-pointer"
                                disabled={
                                  isDownloading &&
                                  currentActionOfferteId === offerte.id
                                }
                              >
                                <Trash2 className="mr-2 h-4 w-4" /> Verwijderen
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </Card>
      </motion.div>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="border-red-200 bg-white rounded-xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 font-bold">
              Offerte Verwijderen
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Weet u zeker dat u offerte{" "}
              <span className="font-semibold text-gray-900">
                {offerteToDelete?.nummer || offerteToDelete?.id}
              </span>{" "}
              voor{" "}
              <span className="font-semibold text-gray-900">
                {offerteToDelete?.klantNaam}
              </span>{" "}
              wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 mt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="border-gray-300 text-gray-700 hover:bg-gray-50 rounded-md"
            >
              Annuleren
            </Button>
            <Button
              onClick={confirmDeleteOfferte}
              className="bg-red-600 hover:bg-red-700 text-white rounded-md"
              disabled={isDownloading}
            >
              <Trash2 className="mr-2 h-4 w-4" /> Verwijderen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OffertesManager;
